import { Product } from '@/domain/products/types';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Tab, Tabs } from '@andritzot/metris-web-ui/navigation/tabs';
import { Card, CardContent } from '@andritzot/metris-web-ui/surfaces/card';
import { useState } from 'react';

import BenefitsContent from './BenefitsContent';
import EndUsersContent from './EndUsersContent';
import GroupContent from './GroupContent';
import IndustriesContent from './IndustriesContent';
import PlantLifeCycleContent from './PlantLifeCycleContent';

interface OverviewTabsWithChipsContentProps {
  product: Product;
}

export const OverviewTabsWithChipsContent = ({ product }: OverviewTabsWithChipsContentProps) => {
  const tabs = [
    {
      label: 'INDUSTRIES',
      hasValue: product.industries?.length > 0,
      content: <IndustriesContent productIndustries={product.industries} />,
    },
    {
      label: 'END USERS',
      hasValue: product.userPersonas?.length > 0,
      content: <EndUsersContent ProductUsers={product.userPersonas} />,
    },
    {
      label: 'BENEFITS',
      hasValue: product.benefitGroups?.length > 0,
      content: <BenefitsContent benefitGroups={product.benefitGroups} />,
    },
    {
      label: 'PLANT LIFECYCLE',
      hasValue: product.lifeCycles?.length > 0,
      content: <PlantLifeCycleContent ProductLifeCycles={product.lifeCycles} />,
    },
    {
      label: 'GROUP',
      hasValue: Boolean(product.group?.productGroup),
      content: <GroupContent ProductGroup={product.group} />,
    },
  ].filter((tab) => tab.hasValue);

  // Keyed by label rather than index: the tab list is derived from the product and this component
  // is not remounted when another product is selected, so a stored index can point past the end.
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  if (tabs.length === 0) {
    return null;
  }

  const activeTab = tabs.find((tab) => tab.label === activeLabel) ?? tabs[0];

  return (
    <Card sx={{ my: 4, backgroundColor: '#fff', width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab.label}
          onChange={(_, v) => setActiveLabel(v as string)}
          variant="scrollable"
          scrollButtons="auto"
          type="secondary"
        >
          {tabs.map((tab) => (
            <Tab key={tab.label} label={tab.label} value={tab.label} />
          ))}
        </Tabs>
      </Box>
      <CardContent>
        {tabs.map((tab) => (
          <Box key={tab.label} role="tabpanel" hidden={tab.label !== activeTab.label}>
            {tab.content}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};
